import {Checkbox} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import type {ChangeEvent} from 'react';
import React, {useEffect, useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {signInWithRedirectUser} from 'src/redux/slices/authSlice';
import {useLocalStorage} from 'usehooks-ts';

export const IdentityProviderRedirect: React.FC = () => {
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const isLoading = isLocalLoading;

  const [skipWarning, setSkipWarning] = useLocalStorage(
    'skip-sso-redirect-warning',
    false
  );

  const [isRedirecting, setIsRedirecting] = useState(false);

  const [validationMessage, setValidationMessage] = useState<
    string | undefined
  >();

  const {identityProvider} = useAppSelector(state => state.auth);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (skipWarning === true) {
      setIsRedirecting(true);
    }
  }, []);

  useEffect(() => {
    const redirect = async () => {
      if (identityProvider === null || isRedirecting !== true) {
        return;
      }

      try {
        // Delegate to the thunk, which clears any stale session before the
        // redirect (the same Cognito bug guard used for username/password
        // sign-in). unwrap() surfaces a rejection here so we can show a message.
        await dispatch(signInWithRedirectUser(identityProvider)).unwrap();
      } catch {
        setValidationMessage(
          'An unexpected error occured. Please try again later or contact your administrator if the issue persists.'
        );
      }
    };

    void redirect();
  }, [isRedirecting]);

  const handleContinueClick = async () => {
    try {
      if (identityProvider === null) {
        setValidationMessage(
          'An unexpected issue occured. We are not able to locate your single sign-on provider. Please contact support.'
        );
        return;
      }

      setIsLocalLoading(true);
      setIsRedirecting(true);
    } catch {
      setValidationMessage(
        'An unexpected error occured. Please try again later or contact your administrator if the issue persists.'
      );
    }
    setIsLocalLoading(false);
  };

  return (
    <>
      {validationMessage && (
        <Alert mb="md" color="red">
          {validationMessage}
        </Alert>
      )}
      {isRedirecting === true && <Box>Redirecting...</Box>}
      {isRedirecting === false && (
        <Box>
          <Alert mb="md" color="yellow">
            <p>
              You are being redirected to your company's login page to complete
              your login. Please follow these steps to continue logging into
              Proact:
            </p>

            <Box>
              • Click 'Continue' to be redirected to your company's login page.
            </Box>
            <Box>
              • Login using your <b>company</b> username and password.
            </Box>
            <Box>
              • After successfully logging in, you'll be returned to Proact and
              can continue your work.
            </Box>
          </Alert>

          <Checkbox
            label="Do not show this message again"
            checked={skipWarning}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setSkipWarning(e.currentTarget.checked)
            }
          />

          <Button
            fullWidth
            mt="xl"
            loading={isLoading}
            onClick={() => handleContinueClick()}>
            Continue
          </Button>
        </Box>
      )}
    </>
  );
};
